import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API } from '@/App';
import { onAuthCleared } from '@/lib/authStorage';

const LocationContext = createContext(null);

export const useLocation = () => useContext(LocationContext);

// Map country codes to languages
const countryToLanguage = {
  'MX': 'es', // Mexico -> Spanish
  // All other countries default to English
};

export const LocationProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [userLocation, setUserLocation] = useState({
    countryCode: 'US',
    countryName: 'United States',
    paymentProvider: 'stripe',
    loading: true
  });
  
  // Initialize from localStorage to prevent race condition
  const [selectedCountry, setSelectedCountry] = useState(() => {
    const saved = localStorage.getItem('xpert_country');
    return saved || 'GLOBAL';
  });
  const [countries, setCountries] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load saved language on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('xpert_language');
    if (savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
    setIsInitialized(true);
  }, [i18n]);

  // Change language when country changes (only after initialization)
  useEffect(() => {
    if (!isInitialized) return;

    const language = countryToLanguage[selectedCountry] || 'en';
    i18n.changeLanguage(language);
    // Store preference
    localStorage.setItem('xpert_language', language);
    localStorage.setItem('xpert_country', selectedCountry);
  }, [selectedCountry, i18n, isInitialized]);

  // P0-3: when auth state is cleared (logout, or login as a different user),
  // reset our in-memory country/language so the next user doesn't inherit them.
  useEffect(() => {
    return onAuthCleared(() => {
      setSelectedCountry('GLOBAL');
      i18n.changeLanguage('en');
    });
  }, [i18n]);

  useEffect(() => {
    // Detect user location for payment routing
    const detectLocation = async () => {
      try {
        const response = await axios.get(`${API}/detect-location`);
        setUserLocation({
          countryCode: response.data.country_code,
          countryName: response.data.country_name,
          paymentProvider: response.data.payment_provider,
          loading: false
        });
      } catch (error) {
        console.error('Location detection failed:', error);
        setUserLocation(prev => ({ ...prev, loading: false }));
      }
    };

    // Fetch supported countries
    const fetchCountries = async () => {
      try {
        const response = await axios.get(`${API}/countries`);
        setCountries(response.data.countries);
      } catch (error) {
        console.error('Failed to fetch countries:', error);
      }
    };

    detectLocation();
    fetchCountries();
  }, []);

  // Get current language
  const currentLanguage = i18n.language || 'en';

  return (
    <LocationContext.Provider value={{
      userLocation,
      selectedCountry,
      setSelectedCountry,
      countries,
      isIndianUser: userLocation.countryCode === 'IN',
      paymentProvider: userLocation.paymentProvider,
      currentLanguage,
      isSpanish: currentLanguage === 'es'
    }}>
      {children}
    </LocationContext.Provider>
  );
};

export default LocationContext;
